import React, { useContext, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import { ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import Select from 'react-select';
import IconsContext from '../../Context/IconsContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

import ModalLayout from '../ModalLayout';

import { BASIC_SELECT_STYLES } from '../../Styles/SelectStyles';
import { otaTypes } from '../../Constants/OtaTypes';

const baseStyle = {
  backgroundColor: '#fafafa',
  borderWidth: 3,
  borderRadius: 5,
  borderColor: 'var(--brand-primary)',
  borderStyle: 'dashed',
  color: 'var(--muted-gray)',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const acceptStyle = { borderColor: 'var(--alert-confirmation)' };
const focusedStyle = { borderColor: 'var(--brand-primary-alert)' };
const rejectStyle = { borderColor: 'var(--warning-yellow)' };

export default function RawAPIModal(props) {
  const { handleFileSelect, file } = props;

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    fileRejections.forEach((file) => {
      file.errors.forEach((err) => {
        props.setError(`Error: ${err.message}`);
      });
    });

    if (acceptedFiles.length) {
      handleFileSelect(acceptedFiles);
    }
  }, []);

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
    isFocused,
  } = useDropzone({
    maxSize: 5242880 /* 5MB */,
    maxFiles: 1,
    multiple: false,
    onDrop,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );
  const { commandControlIcon } = useContext(IconsContext);

  return (
    <ModalLayout
      centered
      isOpen={props.isOpen}
      size="lg"
      toggle={props.toggleModal}
    >
      <ModalHeader className="pb-0 border-bottom-0" toggle={props.toggleModal}>
        <img
          src={commandControlIcon}
          alt="command icon"
          className="img-fluid mx-auto"
          style={{ height: '4rem' }}
        />
        <p className="mb-0 modal-title-text text-center">
          New Customer OTA File
        </p>
      </ModalHeader>
      <ModalBody className="container-fluid d-flex flex-column">
        <div className="row justify-content-center mt-md-3">
          <div className="col-12 col-md-10 d-flex justify-content-center">
            <div className="form-group">
              <label htmlFor="otaType" className="modal-inputlabel-text">
                Choose OTA Type
              </label>
              <Select
                isSearchable
                maxMenuHeight={150}
                name="otaType"
                onChange={props.setOTAType}
                options={otaTypes}
                styles={BASIC_SELECT_STYLES}
                value={props.otaType}
              />
            </div>
          </div>
        </div>
        <div className="row justify-content-center mt-md-3">
          <p className="col-12 col-md-10 text-left text-muted">
            The file will be uploaded to the platform and made available in the
            customer OTA list. When run, the gateway will download the OTA file
            and execute it.
          </p>
        </div>
        <div className="row justify-content-center mt-md-3">
          <p className="col-12 col-md-10 text-left text-muted">
            If this upload creates more than the maximum number of OTAs stored
            on the platform (5), the oldest OTA will be deleted from the
            platform. Uploading a same-named file twice will overwrite the
            existing file.
          </p>
        </div>
        <div className="row justify-content-center mt-md-3">
          <p className="col-12 col-md-10 text-left text-muted">
            The platform will automatically delete OTAs older than six months.
          </p>
        </div>
        {file ? (
          <div className="row justify-content-center mt-md-3">
            <p className="col-12 col-md-10 text-center text-dark">
              selected file: {file.name} {file.size} bytes
            </p>
          </div>
        ) : (
          <div className="flex-fill row justify-content-center mt-md-3">
            <div
              {...getRootProps({ style })}
              className="col-12 col-md-10 d-flex flex-column align-items-center justify-content-center"
            >
              <input {...getInputProps()} />
              <p className="mb-0">
                {isDragActive
                  ? 'Drop the file here ...'
                  : `Drag 'n' drop a file here, or click to select one`}
              </p>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-between border-0 px-md-4">
        <div
          className={props.error ? 'text-danger three-quarter-text' : 'd-none'}
        >
          {props.error}
        </div>
        <button
          className="btn btn-outline-secondary px-4 py-0 nine-tenth-text"
          onClick={props.toggleModal}
        >
          Cancel
        </button>
        <button
          className="btn modal-button-submit px-4 py-0 nine-tenth-text"
          disabled={!props.file || !props.otaType || !props.fileDataBase64}
          onClick={props.handleFileUpload}
        >
          {props.uploadPending ? (
            <FontAwesomeIcon icon={faCircleNotch} spin size="lg" />
          ) : (
            'Upload'
          )}
        </button>
      </ModalFooter>
      <style jsx global>{`
        .modal-title {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          justify-content: center;
          margin-right: -1.5rem;
        }
      `}</style>
    </ModalLayout>
  );
}

RawAPIModal.propTypes = {
  error: PropTypes.string,
  file: PropTypes.object,
  fileDataBase64: PropTypes.string,
  uploadPending: PropTypes.bool,
  otaType: PropTypes.object,
  handleFileSelect: PropTypes.func,
  handleFileUpload: PropTypes.func,
  setOTAType: PropTypes.func,
  setError: PropTypes.func,
  isOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
};

RawAPIModal.defaultProps = {};
